import { useThirdPartyConfigQuery } from "@/lib/queries/third-party-queries";
import { useSelfQuery } from "@/lib/queries/user-queries";
import { useUser } from "@clerk/react";
import { Navigate, useSearchParams } from "react-router";
import {
  ThirdPartyVideoDialogError,
  ThirdPartyVideoDialogPublic,
  ThirdPartyVideoDialogTeam,
} from "./ThirdPartyVideoDialog/ThirdPartyVideoDialog";
import { getLatestTeamExternalId } from "@/lib/local-storage";
import { paths } from "@/App";
import {
  TP_VIDEO_INTERNAL_NAME_STORAGE_KEY,
  TP_VIDEO_TOKEN_STORAGE_KEY,
} from "@/lib/session-storage";
import { useEffect } from "react";

type PublicProps = {
  isPublic: boolean;
};

export function ThirdPartyVideoPage({ isPublic }: PublicProps) {
  const { isLoaded: isUserLoaded, isSignedIn, user } = useUser();
  const {
    data: selfData,
    isPending: isSelfPending,
    isSuccess: isSelfSuccess,
  } = useSelfQuery();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const {
    data: thirdPartyConfig,
    isPending: isThirdPartyConfigPending,
    isSuccess: isThirdPartyConfigSuccess,
  } = useThirdPartyConfigQuery(token, user?.id ?? null);

  useEffect(() => {
    sessionStorage.removeItem(TP_VIDEO_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(TP_VIDEO_INTERNAL_NAME_STORAGE_KEY);
  }, []);

  if (
    !isUserLoaded ||
    (isSignedIn && isSelfPending) ||
    (token && isThirdPartyConfigPending)
  ) {
    return null;
  }

  if (!token || !isThirdPartyConfigSuccess) {
    return (
      <ThirdPartyVideoPageBase>
        <ThirdPartyVideoDialogError
          title="Invalid token"
          description="The provided token is invalid or has expired."
        />
      </ThirdPartyVideoPageBase>
    );
  }

  if (isSignedIn && !isSelfSuccess) {
    return (
      <ThirdPartyVideoPageBase>
        <ThirdPartyVideoDialogError
          title="Failed to load user details"
          description="Please try again later or contact support if the problem persists."
        />
      </ThirdPartyVideoPageBase>
    );
  }

  if (isSignedIn && isPublic) {
    const latestTeamExternalId = getLatestTeamExternalId(
      selfData!.teamMemberships,
    );

    if (!latestTeamExternalId) {
      throw new Error("Found signed in user without a team");
    }

    return (
      <Navigate
        to={paths.thirdPartyVideoTeam(latestTeamExternalId, token)}
        replace
      />
    );
  }

  if (isPublic) {
    return (
      <ThirdPartyVideoPageBase>
        <ThirdPartyVideoDialogPublic
          thirdPartyConfig={thirdPartyConfig}
          token={token}
        />
      </ThirdPartyVideoPageBase>
    );
  }

  return (
    <ThirdPartyVideoPageBase>
      <ThirdPartyVideoDialogTeam
        thirdPartyConfig={thirdPartyConfig}
        token={token}
      />
    </ThirdPartyVideoPageBase>
  );
}

function ThirdPartyVideoPageBase({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative h-full w-full">
      <div className="absolute inset-0">
        <video
          src="/StudioBackground.mp4"
          poster="/StudioBackground.jpg"
          autoPlay
          muted
          loop
          playsInline
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
      </div>

      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-4">
        <div className="w-full overflow-y-auto rounded-xl border bg-background px-10 py-8 shadow-lg sm:w-100">
          {children}
        </div>
      </div>
    </div>
  );
}
